import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { jwtDecode } from "jwt-decode";
import { useGetPhoneNUmberQuery } from "../store/services/whatsappApi";

/* ---------- Quality config ----------
   level = how many of the 3 meter segments are filled */
const QUALITY = {
  GREEN: {
    label: "High quality",
    hint: "Healthy. You can keep sending as usual.",
    level: 3,
    main: "#15803D",
    soft: "#F0FDF4",
    border: "#BBF7D0",
  },
  YELLOW: {
    label: "Medium quality",
    hint: "Needs attention. Fewer blocks and reports will bring it back up.",
    level: 2,
    main: "#B45309",
    soft: "#FFFBEB",
    border: "#FDE68A",
  },
  RED: {
    label: "Low quality",
    hint: "At risk. Sending limits or restrictions may apply.",
    level: 1,
    main: "#B91C1C",
    soft: "#FEF2F2",
    border: "#FECACA",
  },
  UNKNOWN: {
    label: "Not rated yet",
    hint: "Meta has not assigned a rating to this number.",
    level: 0,
    main: "#475569",
    soft: "#F8FAFC",
    border: "#E2E8F0",
  },
};

const getQuality = (rating) =>
  QUALITY[String(rating || "UNKNOWN").toUpperCase()] || QUALITY.UNKNOWN;

/* ---------- Active / Inactive config ---------- */
const STATUS = {
  ACTIVE: {
    label: "Active",
    main: "#15803D",
    soft: "#F0FDF4",
    border: "#BBF7D0",
  },
  INACTIVE: {
    label: "Inactive",
    main: "#B91C1C",
    soft: "#FEF2F2",
    border: "#FECACA",
  },
};

/* ---------- 3-segment quality meter ---------- */
const QualityMeter = ({ level, color }) => (
  <Stack direction="row" spacing={0.5} aria-hidden="true" sx={{ width: 84 }}>
    {[1, 2, 3].map((segment) => (
      <Box
        key={segment}
        sx={{
          flex: 1,
          height: 7,
          borderRadius: 50,
          backgroundColor: segment <= level ? color : "#E2E8F0",
        }}
      />
    ))}
  </Stack>
);

/* ---------- Active/Inactive status pill ---------- */
const StatusPill = ({ isActive }) => {
  const s = isActive ? STATUS.ACTIVE : STATUS.INACTIVE;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 0.75,
        py: 0.15,
        borderRadius: 99,
        fontSize: "0.68rem",
        fontWeight: 700,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        color: s.main,
        backgroundColor: s.soft,
        border: `1px solid ${s.border}`,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: s.main,
          flexShrink: 0,
        }}
      />
      {s.label}
    </Box>
  );
};

/* ---------- Copy button ---------- */
const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      setCopied(false);
    }
  };

  return (
    <Tooltip title={copied ? "Copied" : label} placement="top" arrow>
      <IconButton
        size="small"
        onClick={handleCopy}
        aria-label={label}
        sx={{ color: copied ? "#15803D" : "#64748B" }}
      >
        {copied ? (
          <CheckRoundedIcon sx={{ fontSize: 16 }} />
        ) : (
          <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

/* ---------- One card per phone number ---------- */
const PhoneCard = ({ phone }) => {
  const rating = String(phone.quality_rating || "UNKNOWN").toUpperCase();
  const quality = getQuality(rating);
  const isActive = Boolean(phone.is_active);

  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 1,
        borderColor: "#DCE4E9",
        borderTop: `4px solid ${quality.main}`,
        backgroundColor: "#fff",
        overflow: "hidden",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          borderColor: quality.border,
          borderTopColor: quality.main,
          boxShadow: "0 6px 20px rgba(15, 23, 42, 0.08)",
        },
      }}
    >
      {/* Number + Active/Inactive status */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ p: 2, pb: 1.5 }}
      >
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            width: 38,
            height: 38,
            flexShrink: 0,
            borderRadius: "50%",
            color: "#168447",
            backgroundColor: "#E8F5EC",
          }}
        >
          <WhatsAppIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flexWrap: "wrap", rowGap: 0.5 }}
          >
            <Typography
              sx={{
                fontSize: "1.05rem",
                fontWeight: 700,
                lineHeight: 1.25,
                wordBreak: "break-word",
              }}
            >
              {phone.phone_number || "Number unavailable"}
            </Typography>
            <StatusPill isActive={isActive} />
          </Stack>
          {phone.verified_name && (
            <Typography
              noWrap
              sx={{ fontSize: "0.78rem", color: "text.secondary" }}
            >
              {phone.verified_name}
            </Typography>
          )}
        </Box>
        {phone.phone_number && (
          <CopyButton value={phone.phone_number} label="Copy phone number" />
        )}
      </Stack>

      {/* Quality block */}
      <Box
        sx={{
          mx: 2,
          p: 1.5,
          borderRadius: 0,
          backgroundColor: quality.soft,
          border: `1px solid ${quality.border}`,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: "10%",
                backgroundColor: quality.main,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{ fontSize: "0.88rem", fontWeight: 700, color: quality.main }}
            >
              {quality.label}
            </Typography>
          </Stack>
          <QualityMeter level={quality.level} color={quality.main} />
        </Stack>
        <Typography
          sx={{
            mt: 0.75,
            fontSize: "0.78rem",
            lineHeight: 1.45,
            color: "#475569",
          }}
        >
          {quality.hint}
        </Typography>
      </Box>

      {/* Details */}
      <Stack spacing={1} sx={{ p: 2, pt: 1.75 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
              WABA ID
            </Typography>
            <Typography
              sx={{
                fontSize: "0.82rem",
                fontFamily: "monospace",
                fontWeight: 600,
                wordBreak: "break-all",
              }}
            >
              {phone.waba_id || "-"}
            </Typography>
          </Box>
          {phone.waba_id && (
            <CopyButton value={phone.waba_id} label="Copy WABA ID" />
          )}
        </Stack>

        {phone.messaging_limit && (
          <Box>
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
              Messaging limit
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
              {phone.messaging_limit}
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

/* ---------- Loading skeleton cards ---------- */
const SkeletonCard = () => (
  <Paper
    variant="outlined"
    sx={{ p: 2, borderRadius: 1, borderColor: "#DCE4E9" }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
      <Skeleton variant="circular" width={38} height={38} />
      <Skeleton variant="text" width="55%" height={26} />
    </Stack>
    <Skeleton variant="rounded" height={64} sx={{ mb: 2 }} />
    <Skeleton variant="text" width="30%" />
    <Skeleton variant="text" width="70%" />
  </Paper>
);

const gridSx = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: 2,
};

/* ---------- Page ---------- */
const WhatsAppNumberQuality = () => {
  const [customerId, setCustomerId] = useState(null);
  const [customerIdResolved, setCustomerIdResolved] = useState(false);
  const {
    data: phoneData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetPhoneNUmberQuery(customerId, { skip: !customerId });

  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) return;

      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid || null;
      if (id) setCustomerId(id);
    } catch (error) {
      setCustomerId(null);
    } finally {
      setCustomerIdResolved(true);
    }
  }, []);

  const phoneNumbers = phoneData?.phone_numbers || [];

  // Summary counts, shown above the cards
  const counts = phoneNumbers.reduce((acc, phone) => {
    const key = String(phone.quality_rating || "UNKNOWN").toUpperCase();
    const bucket = QUALITY[key] ? key : "UNKNOWN";
    acc[bucket] = (acc[bucket] || 0) + 1;
    return acc;
  }, {});

  // Active / Inactive counts
  const activeCount = phoneNumbers.filter((p) => Boolean(p.is_active)).length;
  const inactiveCount = phoneNumbers.length - activeCount;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, width: "100%", minWidth: 0 }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 1.5,
              color: "#168447",
              backgroundColor: "#E8F5EC",
            }}
          >
            <WhatsAppIcon />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontSize: "1.05rem", fontWeight: 700 }}
            >
              WhatsApp number quality
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.8rem" }}
            >
              Check how each connected number is performing
            </Typography>
          </Box>
        </Stack>
        <Button
          aria-label="Refresh WhatsApp number quality"
          onClick={() => refetch()}
          disabled={!customerId || isFetching}
          variant="outlined"
          size="small"
          sx={{ minWidth: 36, width: 36, height: 36, p: 0 }}
        >
          {isFetching ? (
            <CircularProgress size={17} />
          ) : (
            <RefreshRoundedIcon fontSize="small" />
          )}
        </Button>
      </Stack>

      {/* States */}
      {!customerIdResolved || isLoading ? (
        <Box sx={gridSx}>
          {[1, 2, 3].map((n) => (
            <SkeletonCard key={n} />
          ))}
        </Box>
      ) : !customerId ? (
        <Alert severity="error">
          Unable to identify the signed-in customer. Sign in again to continue.
        </Alert>
      ) : isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Could not load WhatsApp number quality.
        </Alert>
      ) : phoneNumbers.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 5,
            textAlign: "center",
            borderRadius: 2.5,
            borderColor: "#DCE4E9",
          }}
        >
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              width: 52,
              height: 52,
              mx: "auto",
              mb: 1.5,
              borderRadius: "10%",
              color: "#168447",
              backgroundColor: "#E8F5EC",
            }}
          >
            <WhatsAppIcon />
          </Box>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700 }}>
            No WhatsApp numbers connected
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mt: 0.5, fontSize: "0.82rem" }}
          >
            Once you connect a number, its quality rating will show up here.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Summary */}
          <Stack direction="row" flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
            <Typography
              sx={{
                fontSize: "0.82rem",
                fontWeight: 600,
                color: "text.secondary",
                mr: 0.5,
                alignSelf: "center",
              }}
            >
              {phoneNumbers.length}{" "}
              {phoneNumbers.length === 1 ? "number" : "numbers"}
            </Typography>

            {/* Active / Inactive summary */}
            {activeCount > 0 && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.4,
                  borderRadius: 99,
                  fontSize: "0.76rem",
                  fontWeight: 600,
                  color: STATUS.ACTIVE.main,
                  backgroundColor: STATUS.ACTIVE.soft,
                  border: `1px solid ${STATUS.ACTIVE.border}`,
                }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    backgroundColor: STATUS.ACTIVE.main,
                  }}
                />
                {activeCount} active
              </Box>
            )}
            {inactiveCount > 0 && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.4,
                  borderRadius: 99,
                  fontSize: "0.76rem",
                  fontWeight: 600,
                  color: STATUS.INACTIVE.main,
                  backgroundColor: STATUS.INACTIVE.soft,
                  border: `1px solid ${STATUS.INACTIVE.border}`,
                }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    backgroundColor: STATUS.INACTIVE.main,
                  }}
                />
                {inactiveCount} inactive
              </Box>
            )}

            {/* Quality summary */}
            {["GREEN", "YELLOW", "RED", "UNKNOWN"]
              .filter((key) => counts[key])
              .map((key) => (
                <Box
                  key={key}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 0.4,
                    borderRadius: 99,
                    fontSize: "0.76rem",
                    fontWeight: 600,
                    color: QUALITY[key].main,
                    backgroundColor: QUALITY[key].soft,
                    border: `1px solid ${QUALITY[key].border}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "10%",
                      backgroundColor: QUALITY[key].main,
                    }}
                  />
                  {counts[key]} {QUALITY[key].label.toLowerCase()}
                </Box>
              ))}
          </Stack>

          {/* Cards */}
          <Box sx={gridSx}>
            {phoneNumbers.map((phone) => (
              <PhoneCard
                key={
                  phone.id ||
                  phone.phone_number_id ||
                  phone.waba_id ||
                  phone.phone_number
                }
                phone={phone}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default WhatsAppNumberQuality;